import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Campaign } from '../types';

export const exportToCsv = (campaigns: Campaign[], language: string = 'en') => {
  const headers = language === 'ar' 
    ? ['اسم الحملة', 'الحالة', 'تاريخ البدء', 'تاريخ الانتهاء', 'الميزانية', 'المصروف', 'الوصول', 'التفاعل']
    : ['Campaign Name', 'Status', 'Start Date', 'End Date', 'Budget', 'Spent', 'Reach', 'Engagement'];

  const rows = campaigns.map(campaign => [
    campaign.name,
    campaign.status,
    new Date(campaign.startDate).toLocaleDateString(),
    new Date(campaign.endDate).toLocaleDateString(),
    campaign.budget.toLocaleString(),
    campaign.spent.toLocaleString(),
    campaign.reach.toLocaleString(),
    campaign.engagement.toLocaleString()
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `campaigns_export_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToPdf = (campaigns: Campaign[], language: string = 'en') => {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  const title = language === 'ar' ? 'تقرير أداء الحملات' : 'Campaign Performance Report';
  doc.text(title, 14, 22);
  
  // Add date
  doc.setFontSize(11);
  doc.setTextColor(100);
  const dateText = language === 'ar' 
    ? `التاريخ: ${new Date().toLocaleDateString('ar-SA')}`
    : `Date: ${new Date().toLocaleDateString()}`;
  doc.text(dateText, 14, 30);

  // Prepare table data
  const headers = language === 'ar'
    ? [['اسم الحملة', 'الحالة', 'تاريخ البدء', 'تاريخ الانتهاء', 'الميزانية', 'المصروف', 'الوصول']]
    : [['Campaign', 'Status', 'Start Date', 'End Date', 'Budget', 'Spent', 'Reach']];

  const data = campaigns.map(campaign => [
    campaign.name,
    campaign.status,
    new Date(campaign.startDate).toLocaleDateString(),
    new Date(campaign.endDate).toLocaleDateString(),
    `${campaign.budget.toLocaleString()} SAR`,
    `${campaign.spent.toLocaleString()} SAR`,
    campaign.reach.toLocaleString()
  ]);

  // Add table
  autoTable(doc, {
    startY: 35,
    head: headers,
    body: data,
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246] },
    styles: { fontSize: 9 },
    margin: { top: 35 }
  });

  // Save PDF
  doc.save(`campaigns_report_${new Date().toISOString().split('T')[0]}.pdf`);
};

export const exportSingleCampaignToPdf = (campaign: Campaign, language: string = 'en') => {
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(20);
  doc.text(campaign.name, 14, 22);
  
  // Add date
  doc.setFontSize(11);
  doc.setTextColor(100);
  const dateText = language === 'ar' 
    ? `التاريخ: ${new Date().toLocaleDateString('ar-SA')}`
    : `Generated: ${new Date().toLocaleDateString()}`;
  doc.text(dateText, 14, 30);

  let yPos = 40;

  // Campaign Overview
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(language === 'ar' ? 'نظرة عامة' : 'Campaign Overview', 14, yPos);
  yPos += 10;

  doc.setFontSize(10);
  const overview = [
    [language === 'ar' ? 'الحالة:' : 'Status:', campaign.status],
    [language === 'ar' ? 'تاريخ البدء:' : 'Start Date:', new Date(campaign.startDate).toLocaleDateString()],
    [language === 'ar' ? 'تاريخ الانتهاء:' : 'End Date:', new Date(campaign.endDate).toLocaleDateString()],
    [language === 'ar' ? 'الميزانية:' : 'Budget:', `${campaign.budget.toLocaleString()} SAR`],
    [language === 'ar' ? 'المصروف:' : 'Spent:', `${campaign.spent.toLocaleString()} SAR`],
  ];

  autoTable(doc, {
    startY: yPos,
    body: overview,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 120 }
    }
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Performance Metrics
  doc.setFontSize(14);
  doc.text(language === 'ar' ? 'مقاييس الأداء' : 'Performance Metrics', 14, yPos);
  yPos += 10;

  const metrics = [
    [language === 'ar' ? 'الوصول:' : 'Reach:', campaign.reach.toLocaleString()],
    [language === 'ar' ? 'التفاعل:' : 'Engagement:', campaign.engagement.toLocaleString()],
    [language === 'ar' ? 'معدل النقر:' : 'CTR:', `${(campaign.engagement * 0.1).toFixed(2)}%`],
  ];

  autoTable(doc, {
    startY: yPos,
    body: metrics,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 120 }
    }
  });

  // Save PDF
  doc.save(`${campaign.name.replace(/\s+/g, '_')}_report_${new Date().toISOString().split('T')[0]}.pdf`);
};
